import styled from "styled-components";
import regular from "./assets/BananaGrotesk-Light.otf";
import mono from "./assets/MNKYBananaGroteskMono-Light-Trial.otf";

// Same light + number-only mono faces as exchange-frontend. Unique family names
// keep this embedded customer view from changing the surrounding admin UI.
export const View = styled.section`
  @font-face {
    font-family: AffiliateDashboardFont;
    src: url(${regular}) format("opentype");
    font-weight: 100 900;
  }
  @font-face {
    font-family: AffiliateDashboardFont;
    src: url(${mono}) format("opentype");
    unicode-range: U+30-39;
    font-weight: 100 900;
  }
  @font-face {
    font-family: AffiliateDashboardMono;
    src: url(${mono}) format("opentype");
    font-weight: 100 900;
  }
  width: 100%;
  min-width: 0;
  background: #06060c;
  color: #fff;
  font-family: AffiliateDashboardFont, sans-serif;
  font-size: 14px;
  line-height: 20px;
  padding: 16px 20px 24px;
  &,
  * {
    box-sizing: border-box;
  }
  button,
  input,
  select,
  textarea {
    font-family: inherit;
  }
  button {
    cursor: pointer;
  }
  button:disabled {
    cursor: not-allowed;
  }
  > .dashboard-content {
    width: 100%;
    max-width: 1480px;
    margin: auto;
    min-width: 0;
  }
  h2 {
    font-size: 22px;
    line-height: 28px;
    font-weight: 300;
    margin: 0 0 16px;
  }
  h3 {
    font-size: 14px;
    line-height: 16px;
    margin: 24px 0 12px;
  }
  p {
    color: #a5a5a8;
    line-height: 1.6;
    margin: 12px 0;
  }
  a {
    color: #4cd7f4;
  }
  a:focus-visible,
  summary:focus-visible {
    outline: 2px solid #8856ff;
    outline-offset: 4px;
  }
  details > summary {
    cursor: pointer;
  }
  details + details {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #1a1a1f;
  }
  .read-only-label {
    display: block;
    color: #a5a5a8;
    font-size: 12px;
    margin: 0 auto 12px;
  }
  .wallet-identity {
    display: block;
    color: #6a6a6d;
    font-size: 12px;
    margin-top: 8px;
    overflow-wrap: anywhere;
  }
  @media (min-width: 2400px) {
    > .dashboard-content {
      max-width: 3200px;
    }
  }
  @media (max-width: 760px) {
    padding: 16px 16px 32px;
    h2 {
      font-size: 20px;
      line-height: 24px;
    }
  }
`;
export const Card = styled.div`
  padding: 24px 0;
  margin: 0;
  border-bottom: 1px solid #1a1a1f;
  min-width: 0;
  max-width: 100%;
  @media (max-width: 640px) {
    padding: 16px 0;
  }
`;
export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(210px, 100%), 1fr));
  gap: 16px;
`;
export const Metric = styled.div`
  small {
    display: block;
    font-size: 12px;
    color: #a5a5a8;
    margin-bottom: 12px;
  }
  strong {
    font-size: 28px;
    line-height: 32px;
    font-weight: 500;
    overflow-wrap: anywhere;
  }
`;
export const Button = styled.button.attrs({ type: "button" })<{
  $secondary?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  font-size: 13px;
  line-height: 16px;
  gap: 8px;
  padding: 12px 16px;
  text-decoration: none;
  color: ${({ $secondary }) => ($secondary ? "#fff" : "#000")};
  background: ${({ $secondary }) => ($secondary ? "#18181e" : "#4cd7f4")};
  min-height: 40px;
  min-width: 0;
  border-radius: 8px;
  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }
  &:focus-visible {
    outline: 2px solid #8856ff;
    outline-offset: 3px;
  }
`;
export const Notice = styled.div<{ $error?: boolean }>`
  border: 1px solid
    ${({ $error }) => ($error ? "rgba(252,10,84,.24)" : "#2b2b30")};
  background: ${({ $error }) =>
    $error ? "rgba(252,10,84,.08)" : "rgba(255,255,255,.04)"};
  color: ${({ $error }) => ($error ? "#fc0a54" : "#a5a5a8")};
  padding: 12px 16px;
  border-radius: 8px;
  margin: 16px 0;
  font-size: 13px;
  line-height: 1.6;
`;
export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  && > table th,
  && > table td {
    text-transform: none;
    padding: 0 8px;
    height: 40px;
  }
  && > table th {
    height: 32px;
    color: #6a6a6d;
  }
  && > table td button {
    min-height: 32px;
    height: 32px;
    padding: 0 12px;
  }
  && > table th:first-child,
  && > table td:first-child {
    padding-left: 0;
  }
`;
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  line-height: 16px;
  th {
    color: #a5a5a8;
    font-weight: 400;
    white-space: nowrap;
    text-transform: capitalize;
  }
  th,
  td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #1a1a1f;
    vertical-align: middle;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
  tbody tr:hover {
    background: #18181e;
  }
  td small {
    display: block;
    color: #a5a5a8;
    margin-top: 4px;
    max-width: 260px;
    overflow-wrap: anywhere;
  }
`;
export const Address = styled.span`
  font-size: 12px;
  line-height: 16px;
  overflow-wrap: anywhere;
`;
export const DialogBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;
export const DialogPanel = styled.div`
  width: 620px;
  max-width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  border: 1px solid #2b2b30;
  border-radius: 16px;
  padding: 24px;
  background: #0e0e14;
  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  .dialog-header h2 {
    margin: 0;
  }
  .dialog-header button {
    color: #fff;
    background: #18181e;
    border-radius: 8px;
    min-width: 32px;
    min-height: 32px;
    font-size: 20px;
  }
  .referral-url {
    overflow-wrap: anywhere;
  }
  @media (max-width: 760px) {
    padding: 20px;
  }
`;
